/**
 * StatCard - Single KPI display with optional trend
 *
 * Usage:
 *   <StatCard data={{ value: 8241, label: 'Steps', trend: 12, icon: '👟' }} />
 */

import type { BlockProps, StatCardData } from './types';
import { getTrendColor } from './colors';
import './blocks.css';

export interface StatCardProps extends BlockProps<StatCardData> {
  /** Compact mode for inline display */
  compact?: boolean;
  /** Click handler */
  onClick?: () => void;
}

export function StatCard({ data, title, compact, onClick, className }: StatCardProps) {
  const { value: rawValue, label, unit, trend: rawTrend, icon, subtitle } = data;

  // Guard against undefined/null values from LLM-generated card data
  const value = rawValue ?? 0;
  // Coerce trend to number — agents may send strings like '+5'
  const trendNum = rawTrend != null ? Number(rawTrend) : undefined;
  const trend = trendNum != null && !Number.isNaN(trendNum) ? trendNum : undefined;

  const formattedValue = typeof value === 'number'
    ? value.toLocaleString()
    : value;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  const trendDisplay = trend !== undefined && trend !== null && (
    <span
      className="stat-card-trend"
      style={{ color: getTrendColor(trend) }}
    >
      {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'}
      {Math.abs(trend)}%
    </span>
  );

  if (compact) {
    return (
      <div
        className={`stat-card stat-card--compact ${className || ''}`}
        onClick={onClick}
        onKeyDown={onClick ? handleKeyDown : undefined}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={onClick ? `${label}: ${formattedValue}${unit ? ` ${unit}` : ''}` : undefined}
      >
        {icon && <span className="stat-card-icon">{icon}</span>}
        <span className="stat-card-value">{formattedValue}</span>
        {unit && <span className="stat-card-unit">{unit}</span>}
        {trendDisplay}
      </div>
    );
  }

  return (
    <div
      className={`stat-card ${className || ''}`}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `${label}: ${formattedValue}${unit ? ` ${unit}` : ''}` : undefined}
    >
      {title && <div className="stat-card-title">{title}</div>}

      <div className="stat-card-header">
        {icon && <span className="stat-card-icon">{icon}</span>}
        <span className="stat-card-label">{label}</span>
      </div>

      <div className="stat-card-body">
        <span className="stat-card-value">{formattedValue}</span>
        {unit && <span className="stat-card-unit">{unit}</span>}
        {trendDisplay}
      </div>

      {subtitle && <div className="stat-card-subtitle">{subtitle}</div>}
    </div>
  );
}

export default StatCard;
